import { Suspense } from 'react';
import { SKELETON_PROPS } from '@/lib/constants/skeleton';
import OutOfStockSlash from './OutOfStockSlash';
import { useSwatchSelection } from './use-swatch-selection';

const SWATCH_BUTTON =
  'relative block h-12 w-12 rounded-full border focus:outline-none';

// The ring around a swatch
const RING =
  'after:absolute after:-top-1.5 after:-left-1.5 after:h-14.5 after:w-14.5 after:rounded-full after:border-2 after:content-[""]';
const HOVER_RING =
  'hover:after:absolute hover:after:-top-1.5 hover:after:-left-1.5 hover:after:h-14.5 hover:after:w-14.5 hover:after:rounded-full hover:after:border-2 hover:after:content-[""] hover:after:border-parlor-green-60';
const FOCUS_RING =
  'focus:after:absolute focus:after:-top-1.5 focus:after:-left-1.5 focus:after:h-14.5 focus:after:w-14.5 focus:after:rounded-full focus:after:border-2 focus:after:content-[""] focus:after:border-parlor-green-60';

/**
 * Color swatches drawn as flat circles of a Contentstack-picked hex color.
 *
 * The swatches themselves come from the CMS, so they render in the static
 * shell; only the selected ring, the out-of-stock marks and the click handler
 * wait on the URL.
 */
export default function HexSwatches({ selectorSettings, optionName }) {
  const swatches = (selectorSettings.hex_colors ?? []).map((item) => ({
    value: item.color_title,
    color: item.button_color_picker
  }));

  return (
    <Suspense
      fallback={<HexSwatchGrid swatches={swatches} {...SKELETON_PROPS} />}
    >
      <SelectedHexSwatchGrid swatches={swatches} optionName={optionName} />
    </Suspense>
  );
}

/**
 * Reads the selected variant, so it needs to stay inside a Suspense boundary.
 * Renders the same grid as the fallback with the selection filled in.
 */
const SelectedHexSwatchGrid = ({ swatches, optionName }) => {
  const selection = useSwatchSelection(optionName);

  return (
    <HexSwatchGrid
      swatches={swatches}
      selection={selection}
      data-cy="hex-swatches-selector"
    />
  );
};

/**
 * The grid itself, presentational
 */
const HexSwatchGrid = ({ swatches, selection, ...fieldsetProps }) => (
  <fieldset {...fieldsetProps}>
    <legend className="sr-only">Select color</legend>
    <div
      className="flex w-full flex-wrap gap-2.5 sm:gap-2"
      role="radiogroup"
      aria-label="Color options"
    >
      {swatches.map(({ value, color }) => {
        const isSelected = selection?.currentValue === value;
        const isOutOfStock = selection?.isOutOfStock(value) ?? false;

        return (
          <div
            className="relative flex flex-col items-center pr-2.5 sm:mr-0"
            key={value}
          >
            <button
              type="button"
              className={
                isSelected
                  ? `${SWATCH_BUTTON} ${RING} after:border-light-green`
                  : `${SWATCH_BUTTON} cursor-pointer ${HOVER_RING} ${FOCUS_RING} ${isOutOfStock ? `${RING} after:border-parlor-green-60` : ''}`
              }
              style={{ backgroundColor: color, borderColor: color }}
              onClick={selection && (() => selection.selectOption(value))}
              aria-label={`Select ${value} color${isOutOfStock ? ' (out of stock)' : ''}`}
              aria-pressed={selection ? isSelected : undefined}
            >
              {isOutOfStock && (
                <OutOfStockSlash
                  containerClassName="absolute inset-0.75"
                  svgClassName="stroke-parlor-green-60 absolute inset-0 h-full w-full"
                />
              )}
            </button>
          </div>
        );
      })}
    </div>
  </fieldset>
);
